import {
  ApiPageType,
  ApiProjectType,
  ApiReservedPageTypes,
} from "@incendium/api";
import { Chip, TextField, Typography, Box, Stack, alpha } from "@mui/material";
import { Autocomplete } from "@mui/material";
import { useHasProjectType } from "features/project";
import { AnimatePresence, motion } from "motion/react";
import { usePageTypes } from "Hooks/usePageTypes";
import { useMemo } from "react";
import { LocalOffer } from "@mui/icons-material";

interface LocationPageTypesFieldProps {
  value: ApiPageType[];
  onChange: (pageTypes: ApiPageType[]) => void;
  onCreateNew?: (name: string) => Promise<ApiPageType>;
  size?: "small" | "medium";
  label?: string;
  disabled?: boolean;
}

function LocationPageTypesField({
  value,
  onChange,
  onCreateNew,
  size = "small",
  label,
  disabled = false,
}: LocationPageTypesFieldProps) {
  const { pageTypes } = usePageTypes();
  const hasProjectType = useHasProjectType();

  const options = useMemo(() => {
    return pageTypes
      .filter(
        (option) =>
          !value.map((pt) => pt.id).includes(option.id)
      )
      .filter((f) => {
        // remove project type related defaults, and re add based on project type
        if (!f._default || f._default === ApiReservedPageTypes.UNSPECIFIED) {
          return true;
        }
        return [
          ApiReservedPageTypes.PRODUCT_PAGE,
          ApiReservedPageTypes.PRODUCT_CATEGORY,
          ApiReservedPageTypes.CHECKOUT,
        ].includes(f._default)
          ? hasProjectType(ApiProjectType.TYPE_ECOMMERCE)
          : [
            ApiReservedPageTypes.JOB_PAGE,
            ApiReservedPageTypes.JOB_CATEGORY,
          ].includes(f._default)
            ? hasProjectType(ApiProjectType.TYPE_RECRUITMENT)
            : [
              ApiReservedPageTypes.SERVICE_CATEGORY,
              ApiReservedPageTypes.SERVICE_PAGE,
            ].includes(f._default)
              ? hasProjectType(ApiProjectType.TYPE_B2_B)
              : true;
      })
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
      .sort((a, b) => (a._default || "").localeCompare(b._default || ""));
  }, [pageTypes, value, hasProjectType]);

  const handleChange = async (values: (string | ApiPageType)[]) => {
    const mappedPageTypes = await Promise.all(
      values.map(async (val) => {
        if (typeof val === "string") {
          // If it's a string, it's a new page type that needs to be created
          if (onCreateNew) {
            return await onCreateNew(val);
          }
          // If no onCreateNew handler, just skip
          return null;
        }
        return val;
      })
    );

    // Filter out any null values (from skipped string values)
    const filteredPageTypes = mappedPageTypes.filter(
      (pt): pt is ApiPageType => pt !== null
    );

    onChange(filteredPageTypes);
  };

  return (
    <Autocomplete
      multiple
      id="pageTypes"
      onChange={(e, values) => handleChange(values)}
      value={value}
      disableClearable
      freeSolo={!!onCreateNew}
      fullWidth
      size={size}
      disabled={disabled}
      options={options || []}
      getOptionLabel={(option) =>
        typeof option === "string"
          ? option
          : option.name || ""
      }
      renderOption={(props, option) => {
        const isReserved = typeof option !== "string" &&
          option._default &&
          option._default !== ApiReservedPageTypes.UNSPECIFIED;

        return (
          <Box component="li" {...props} sx={{ gap: 1 }}>
            <LocalOffer sx={{ fontSize: 16, color: 'text.secondary' }} />
            <Stack direction="row" spacing={1} alignItems="center" flex={1}>
              <Typography variant="body2">
                {typeof option === "string" ? option : option.name}
              </Typography>
              {isReserved && (
                <Chip
                  label="Reserved"
                  size="small"
                  sx={{
                    height: 18,
                    fontSize: '0.65rem',
                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
                    color: 'primary.main',
                    fontWeight: 600,
                  }}
                />
              )}
            </Stack>
          </Box>
        );
      }}
      renderTags={(tagValue, getTagProps) => (
        <AnimatePresence mode="popLayout">
          {tagValue.map((option, index) => {
            const isReserved = option._default &&
              option._default !== ApiReservedPageTypes.UNSPECIFIED;
            const { key, ...tagProps } = getTagProps({ index });

            return (
              <Chip
                key={key}
                size={size}
                variant="outlined"
                component={motion.div}
                initial={{ scale: 0.8, opacity: 0 }}
                exit={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.2 }}
                label={option.name}
                icon={<LocalOffer sx={{ fontSize: '14px !important' }} />}
                sx={{
                  borderColor: 'divider',
                  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                  '& .MuiChip-icon': {
                    color: isReserved ? 'primary.main' : 'text.secondary',
                  },
                  fontWeight: 400,
                }}
                {...tagProps}
              />
            );
          })}
        </AnimatePresence>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          variant="outlined"
          placeholder={value.length === 0 ? "Add tags..." : ""}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <>
                {value.length === 0 && (
                  <LocalOffer sx={{ fontSize: 18, color: 'text.disabled', mr: 1 }} />
                )}
                {params.InputProps.startAdornment}
              </>
            ),

          }}
        />
      )}
    />
  );
}

export default LocationPageTypesField;

